import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { 
  MapPin, Users, ChefHat, Award, Crown, Star, ArrowRight,
  Flame, Trophy, Heart, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

export default function RegionPage() {
  const { regionId } = useParams();
  const [region, setRegion] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [topRecipes, setTopRecipes] = useState([]);
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegionData();
  }, [regionId]);

  const fetchRegionData = async () => {
    try {
      const response = await api.get(`/cultural/regions/${regionId}`);
      setRegion(response.data.region);
      setLeaderboard(response.data.leaderboard || []);
      setTopRecipes(response.data.top_recipes || []);
      setChampions(response.data.champions || []);
    } catch (error) {
      console.error('Error fetching region:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#1E4ED8] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!region) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900">Région non trouvée</h1>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F5F2]" data-testid="region-page">
      {/* Hero Section */}
      <div className="relative h-80 bg-gradient-to-br from-[#1E4ED8] to-[#0D3A9E] overflow-hidden">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-60 border-4 border-white rounded-t-full" />
          <div className="absolute bottom-10 right-20 w-32 h-48 border-4 border-white rounded-t-full" />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 h-full flex flex-col justify-end pb-8">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-2">
            <MapPin className="w-4 h-4" />
            <span>Tunisie</span>
          </div>
          <h1 className="font-fraunces text-4xl md:text-5xl font-bold text-white mb-2">
            {region.name}
          </h1>
          <p className="text-xl text-white/80 font-arabic">{region.name_ar}</p>
          
          {/* Stats */}
          <div className="flex gap-6 mt-6">
            <div className="text-white/90">
              <span className="text-2xl font-bold">{region.total_users?.toLocaleString() || 0}</span>
              <span className="text-sm ml-1">chefs</span>
            </div>
            <div className="text-white/90">
              <span className="text-2xl font-bold">{region.total_recipes?.toLocaleString() || 0}</span>
              <span className="text-sm ml-1">recettes</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-fraunces text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#1E4ED8]" />
                À propos de {region.name}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {region.description || region.culinary_traditions}
              </p>
              
              {/* Spice Level */}
              <div className="flex items-center gap-2 text-sm">
                <Flame className="w-4 h-4 text-[#D9381E]" />
                <span className="font-medium text-gray-700">Niveau d'épice:</span>
                <span className="text-[#D9381E]">{region.spice_preference}</span>
              </div>
            </div>

            {/* Signature Dishes */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-fraunces text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-[#E07A5F]" />
                Plats Signatures
              </h2>
              <div className="flex flex-wrap gap-2">
                {region.signature_dishes?.map((dish, i) => (
                  <span 
                    key={i}
                    className="px-4 py-2 bg-[#E07A5F]/10 text-[#E07A5F] rounded-full font-medium text-sm"
                  >
                    {dish}
                  </span>
                ))}
              </div>
            </div>

            {/* Local Ingredients */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-fraunces text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#4F6F52]" />
                Ingrédients Locaux
              </h2>
              <div className="flex flex-wrap gap-2">
                {region.local_ingredients?.map((ing, i) => (
                  <span 
                    key={i}
                    className="px-4 py-2 bg-[#4F6F52]/10 text-[#4F6F52] rounded-full font-medium text-sm"
                  >
                    {ing}
                  </span>
                ))}
              </div>
            </div>

            {/* Top Recipes */}
            {topRecipes.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="font-fraunces text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#F2A900]" />
                  Meilleures Recettes de {region.name}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {topRecipes.map((recipe) => (
                    <Link 
                      key={recipe.id}
                      to={`/recipes/${recipe.id}`}
                      className="flex gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <img 
                        src={recipe.cover_image || '/placeholder-recipe.jpg'} 
                        alt={recipe.title}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{recipe.title}</h3>
                        <p className="text-sm text-gray-500">par @{recipe.creator_username}</p>
                        <div className="flex items-center gap-1 text-sm text-[#D9381E]">
                          <Heart className="w-3 h-3" />
                          {recipe.likes_count}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Regional Champions */}
            <div className="bg-gradient-to-br from-[#F2A900]/10 to-[#E07A5F]/10 rounded-2xl border border-[#F2A900]/20 p-6">
              <h2 className="font-fraunces text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#F2A900]" />
                Champions de {region.name}
              </h2>
              
              {champions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Pas encore de champion pour cette région. Sois le premier!
                </p>
              ) : (
                <div className="space-y-3">
                  {champions.map((champion) => (
                    <div key={champion.id} className="flex items-center gap-3 p-2 bg-white/50 rounded-lg">
                      <Avatar className="h-10 w-10 border-2 border-[#F2A900]">
                        <AvatarImage src={champion.profile_picture} />
                        <AvatarFallback>{champion.username?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {champion.title}
                        </p>
                        <p className="text-xs text-gray-500">@{champion.username}</p>
                      </div>
                      <Award className="w-5 h-5 text-[#F2A900]" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Leaderboard */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-fraunces text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#1E4ED8]" />
                Top 10 de {region.name}
              </h2>
              
              {leaderboard.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucun chef dans cette région pour le moment
                </p>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((user, index) => (
                    <Link 
                      key={user.id}
                      to={`/u/${user.username}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-[#F2A900] text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-[#CD7F32] text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {index + 1}
                      </span>
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.profile_picture} />
                        <AvatarFallback className="text-xs">{user.username?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{user.display_name}</p>
                      </div>
                      <span className="text-sm font-bold text-[#D9381E]">{user.barka}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <Link to={`/regions/${regionId}/face`}>
              <Button className="w-full bg-[#1E4ED8]">
                <Users className="w-4 h-4 mr-2" />
                Voir le Visage de {region.name}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
